import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/LockOutlined';
import { Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import paths from '../../router/paths';
import { check, checkRoleAccess } from '../../services/check-access';
import { useStateToProps } from '../../store/actions/actions';
import {
  getOrgAppVersion,
  hasOrgAppVersionLoaded,
  ORG_APP_VERSION_V2_2,
} from '../../utils/org-app-version';
import Logo from '../logo';
import { getNdisMenuItems, getSahMenuItems } from './menu-config';
import SidebarSkeleton from './sidebar-skeleton';

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 44;

// Add a styled component for consistent icon sizing
const StyledIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  '& > *': {
    fontSize: '1.25rem',
  },
}));

// Update the StyledListItem component to show lock icon but not disable
const StyledListItem = styled(ListItem)(({ theme, isactive }) => ({
  color: theme.palette.text.secondaryLight,
  position: 'relative',
  minHeight: '40px',
  paddingLeft: '5px',
  textDecoration: 'none',
  '& .menuIcon': {
    color: theme.palette.text.secondaryLight,
  },
  '& .MuiListItemIcon-root': {
    minWidth: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.common.white,
    '& .menuIcon': {
      color: theme.palette.common.white,
    },
  },
  ...(isactive === 'true' && {
    backgroundColor: theme.palette.background.dark,
    color: theme.palette.common.white,
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '3px',
      backgroundColor: theme.palette.primary.main,
    },
    '& .menuIcon': {
      color: theme.palette.common.white,
    },
    '&:hover': {
      backgroundColor: theme.palette.text.primary,
      color: theme.palette.common.white,
      '& .menuIcon': {
        color: theme.palette.common.white,
      },
    },
  }),
}));

const StyledSubMenuItem = styled(StyledListItem)(({ theme }) => ({
  padding: '2px 16px 2px 24px',
  textDecoration: 'none',
}));

const MenuSectionLabel = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondaryLight,
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  lineHeight: 1,
  opacity: 0.65,
  padding: '6px 22px',
  textTransform: 'uppercase',
}));

// Segmented control-like Tabs styling for product switcher
const ProductTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 36,
  height: 36,
  borderRadius: 18,
  backgroundColor: theme.palette.background.dark,
  padding: 4,
  overflow: 'hidden',
  '& .MuiTabs-flexContainer': {
    gap: 6,
  },
  '& .MuiTabs-indicator': {
    top: 0,
    height: '28px',
    borderRadius: 14,
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(['transform', 'left', 'width'], {
      duration: theme.transitions.duration.shortest,
      easing: theme.transitions.easing.easeOut,
    }),
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .MuiTabs-indicator': {
      transition: 'none',
    },
  },
}));

const ProductTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minHeight: 28,
  height: 28,
  minWidth: 0,
  flex: 1,
  borderRadius: 14,
  fontWeight: 600,
  color: theme.palette.text.secondaryLight,
  position: 'relative',
  zIndex: 2,
  transition: theme.transitions.create(['color'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeOut,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    color: theme.palette.common.white,
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

const hasSubMenu = (item) => Array.isArray(item?.subMenu) && item.subMenu.length > 0;

const isLabelItem = (item) => Boolean(item?.isLabel);

const isArModulePath = (path = '') => /^\/app\/ar(?:\/|$)/.test(String(path));

const getNavigationTarget = (to, activeProduct) => {
  if (!to) {
    return to;
  }

  const [pathname, search = ''] = String(to).split('?');

  return {
    pathname,
    search: search ? `?${search}` : '',
    state: { sidebarProduct: activeProduct },
  };
};

const getProductFromPath = (path = '', preferredProduct = null) => {
  if (path.startsWith('/support-at-home')) {
    return 'SAH';
  }

  if (path.startsWith('/ndis/')) {
    return 'NDIS';
  }

  if (isArModulePath(path)) {
    if (preferredProduct === 'NDIS' || preferredProduct === 'SAH') {
      return preferredProduct;
    }
  }

  return null;
};

const getInitialProduct = ({ currentPath, isDualProduct, userLicense }) => {
  if (userLicense === 'supportAtHome') {
    return 'SAH';
  }

  if (!isDualProduct) {
    return 'NDIS';
  }

  return getProductFromPath(currentPath) || 'NDIS';
};

const getPreferredPathForProduct = (product) =>
  product === 'NDIS' ? paths.participants : paths.supportAtHomeCareRecipients;

const getProductMenuItems = ({
  activeProduct,
  isDualProduct,
  labels,
  orgAppVersion,
  userLicense,
}) => {
  if (isDualProduct) {
    return activeProduct === 'NDIS'
      ? getNdisMenuItems(labels, orgAppVersion)
      : getSahMenuItems(labels);
  }

  return userLicense === 'supportAtHome'
    ? getSahMenuItems(labels)
    : getNdisMenuItems(labels, orgAppVersion);
};

const getAccessResult = (item, userLicense, userRole) => {
  if (!item?.permission) {
    return { hasAccess: true, reason: null };
  }

  const roleAllowsAccess = checkRoleAccess(userRole, item.permission);
  if (!roleAllowsAccess) {
    return { hasAccess: false, reason: 'role' };
  }

  const licenseAllowsAccess = check(userLicense, item.permission, {}, userRole);
  if (!licenseAllowsAccess) {
    return { hasAccess: false, reason: 'license' };
  }

  return { hasAccess: true, reason: null };
};

const getAccessRedirect = (page, reason) => {
  if (reason === 'role') {
    return {
      pathname: paths.roleAccess,
      state: { page, reason: 'role' },
    };
  }

  if (reason === 'license') {
    return {
      pathname: paths.licenseAccess,
      state: { page, reason: 'license' },
    };
  }

  return {
    pathname: paths.lock,
    state: { page },
  };
};

const getTooltipTitle = (accessResult, licenseMessage) => {
  if (accessResult.hasAccess) {
    return '';
  }

  return accessResult.reason === 'role'
    ? 'You do not have permission to access this page'
    : licenseMessage;
};

const isActivePath = (currentPath, itemPath = '') => {
  const basePath = itemPath.split('?')[0];

  if (!basePath) {
    return false;
  }

  if (currentPath === paths.lock) {
    return basePath === paths.lock && !basePath.includes('lock');
  }

  if (currentPath === basePath) {
    return true;
  }

  return basePath !== '/' && basePath !== paths.lock && currentPath.startsWith(basePath);
};

const findMenuItemByPath = (items, targetPath) => {
  for (const item of items) {
    if (!isLabelItem(item)) {
      if (item.to === targetPath) {
        return item;
      }

      if (hasSubMenu(item)) {
        const nestedMatch = findMenuItemByPath(item.subMenu, targetPath);
        if (nestedMatch) {
          return nestedMatch;
        }
      }
    }
  }

  return null;
};

const findFirstAccessiblePath = (items, getItemAccess) => {
  for (const item of items) {
    if (!isLabelItem(item)) {
      if (hasSubMenu(item)) {
        const nestedPath = findFirstAccessiblePath(item.subMenu, getItemAccess);
        if (nestedPath) {
          return nestedPath;
        }
      } else {
        const accessResult = getItemAccess(item);
        if (accessResult.hasAccess && item.to && item.to !== paths.lock) {
          return item.to;
        }
      }
    }
  }

  return null;
};

function Sidebar({ isMobile, mobileOpen, drawerOpen, setDrawerOpen, handleDrawerToggle }) {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;
  const currentProductHint = location.state?.sidebarProduct || null;

  const {
    license: userLicense,
    role: userRole,
    labels,
    orgId,
    generalSettings,
  } = useStateToProps((state) => ({
    license: state.app.user?.license || state.app.user?.appMetaData?.license,
    role: state.app.user?.role || state.app.user?.appMetaData?.role || 'basic',
    labels: state.app.labels,
    orgId: state.app.orgId,
    generalSettings: state.app.generalSettings,
  }));

  const isDualProduct = userLicense === 'enterprisePlusSAH';
  const orgAppVersion = useMemo(
    () => getOrgAppVersion(generalSettings, orgId),
    [generalSettings, orgId],
  );
  const [activeProduct, setActiveProduct] = useState(() =>
    getInitialProduct({
      currentPath,
      isDualProduct,
      userLicense,
    }),
  );

  useEffect(() => {
    if (!isDualProduct) {
      return;
    }

    const productFromPath = getProductFromPath(currentPath, currentProductHint);
    if (productFromPath && productFromPath !== activeProduct) {
      setActiveProduct(productFromPath);
    }
  }, [activeProduct, currentPath, currentProductHint, isDualProduct]);
  const isOrgAppVersionLoaded = useMemo(
    () => hasOrgAppVersionLoaded(generalSettings, orgId),
    [generalSettings, orgId],
  );
  const shouldWaitForAppVersion = Boolean(
    userLicense &&
    userLicense !== 'supportAtHome' &&
    (!isDualProduct || activeProduct === 'NDIS') &&
    !isOrgAppVersionLoaded,
  );

  const menuItems = useMemo(
    () =>
      getProductMenuItems({
        activeProduct,
        isDualProduct,
        labels,
        orgAppVersion,
        userLicense,
      }),
    [activeProduct, isDualProduct, labels, orgAppVersion, userLicense],
  );
  const skeletonMenuItems = useMemo(() => {
    if (activeProduct === 'SAH' || userLicense === 'supportAtHome') {
      return getSahMenuItems(labels);
    }

    if (shouldWaitForAppVersion) {
      return getNdisMenuItems(labels, ORG_APP_VERSION_V2_2);
    }

    return menuItems;
  }, [activeProduct, labels, menuItems, shouldWaitForAppVersion, userLicense]);
  const showSidebarSkeleton = !userLicense || shouldWaitForAppVersion;

  const [openSubmenus, setOpenSubmenus] = useState({});
  const drawerWidthCurrent = drawerOpen ? expandedDrawerWidth : collapsedDrawerWidth;
  const drawerTransitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };

  const getItemAccess = useCallback(
    (item) => getAccessResult(item, userLicense, userRole),
    [userLicense, userRole],
  );

  const navigateToAccessPage = useCallback(
    (page, reason) => {
      history.push(getAccessRedirect(page, reason));
    },
    [history],
  );

  const handleMenuClick = useCallback(
    (item) => {
      const accessResult = getItemAccess(item);

      if (!accessResult.hasAccess) {
        navigateToAccessPage(item.title, accessResult.reason);
        return;
      }

      if (hasSubMenu(item)) {
        if (!drawerOpen) {
          setDrawerOpen(true);
          setOpenSubmenus({ [item.key]: true });
          return;
        }

        setOpenSubmenus((prev) => ({
          [item.key]: !prev[item.key],
        }));
        return;
      }

      history.push(getNavigationTarget(item.to, activeProduct));
    },
    [activeProduct, drawerOpen, getItemAccess, history, navigateToAccessPage, setDrawerOpen],
  );

  const handleSubMenuClick = useCallback(
    (event, item, accessResult) => {
      if (accessResult.hasAccess) {
        return;
      }

      event.preventDefault();
      navigateToAccessPage(item.title, accessResult.reason);
    },
    [navigateToAccessPage],
  );

  const isActive = useCallback((itemPath) => isActivePath(currentPath, itemPath), [currentPath]);

  const handleProductChange = useCallback(
    (event, nextProduct) => {
      setActiveProduct(nextProduct);
      setOpenSubmenus({});

      const targetItems = getProductMenuItems({
        activeProduct: nextProduct,
        isDualProduct,
        labels,
        orgAppVersion,
        userLicense,
      });
      const preferredPath = getPreferredPathForProduct(nextProduct);
      const preferredItem = findMenuItemByPath(targetItems, preferredPath);

      if (preferredItem) {
        const preferredAccess = getItemAccess(preferredItem);
        if (preferredAccess.hasAccess && preferredPath !== paths.lock) {
          history.push(preferredPath);
          return;
        }
      }

      const firstAccessiblePath = findFirstAccessiblePath(targetItems, getItemAccess);
      if (firstAccessiblePath) {
        history.push(firstAccessiblePath);
      }
    },
    [getItemAccess, history, isDualProduct, labels, orgAppVersion, userLicense],
  );

  const renderSubMenuItem = (subItem) => {
    if (isLabelItem(subItem)) {
      return <MenuSectionLabel key={subItem.key}>{subItem.title}</MenuSectionLabel>;
    }

    const accessResult = getItemAccess(subItem);
    const { hasAccess } = accessResult;

    return (
      <Tooltip
        key={subItem.key}
        title={getTooltipTitle(accessResult, 'Upgrade your license to access this feature')}
        placement="right"
        arrow
      >
        <div>
          <StyledSubMenuItem
            button
            component={Link}
            to={getNavigationTarget(subItem.to, activeProduct)}
            onClick={(event) => handleSubMenuClick(event, subItem, accessResult)}
            isactive={isActive(subItem.to).toString()}
          >
            <ListItemIcon>{subItem.icon}</ListItemIcon>
            {drawerOpen && <ListItemText primary={subItem.title} />}
            {drawerOpen && !hasAccess && (
              <LockIcon fontSize="small" sx={{ color: theme.palette.text.secondaryLight }} />
            )}
          </StyledSubMenuItem>
        </div>
      </Tooltip>
    );
  };

  const renderMenuItem = (item) => {
    const accessResult = getItemAccess(item);
    const { hasAccess } = accessResult;

    return (
      <React.Fragment key={item.key}>
        <Tooltip
          title={getTooltipTitle(accessResult, 'Upgrade your license to access this page')}
          placement="right"
          arrow
        >
          <div>
            <StyledListItem
              button
              component={hasSubMenu(item) ? 'div' : Link}
              to={!hasSubMenu(item) ? getNavigationTarget(item.to, activeProduct) : undefined}
              onClick={(event) => {
                if (hasSubMenu(item)) {
                  event.preventDefault();
                  handleMenuClick(item);
                  return;
                }

                if (event.metaKey || event.ctrlKey) {
                  return;
                }

                event.preventDefault();
                handleMenuClick(item);
              }}
              isactive={isActive(item.to).toString()}
            >
              <ListItemIcon>
                <StyledIcon>{item.icon}</StyledIcon>
              </ListItemIcon>
              {drawerOpen && <ListItemText primary={item.title} />}
              {drawerOpen && !hasAccess && (
                <LockIcon fontSize="small" sx={{ color: theme.palette.text.secondaryLight }} />
              )}
              {drawerOpen &&
                hasSubMenu(item) &&
                (openSubmenus[item.key] ? <ExpandLess /> : <ExpandMore />)}
            </StyledListItem>
          </div>
        </Tooltip>
        {hasSubMenu(item) && (
          <Collapse in={drawerOpen && openSubmenus[item.key]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subMenuLabel && <MenuSectionLabel>{item.subMenuLabel}</MenuSectionLabel>}
              {item.subMenu.map(renderSubMenuItem)}
            </List>
          </Collapse>
        )}
        {item.divider && <Divider />}
      </React.Fragment>
    );
  };

  const drawer = (
    <div>
      <div style={{ height: '64px', alignContent: 'center' }}>
        <Logo sideMenu={drawerOpen} />
      </div>
      {isDualProduct && drawerOpen && !showSidebarSkeleton && (
        <Box px={1} pb={1}>
          <ProductTabs value={activeProduct} onChange={handleProductChange}>
            <ProductTab value="NDIS" label="NDIS" />
            <ProductTab value="SAH" label="SAH" />
          </ProductTabs>
        </Box>
      )}
      {showSidebarSkeleton ? (
        <SidebarSkeleton
          drawerOpen={drawerOpen}
          isDualProduct={isDualProduct}
          menuItems={skeletonMenuItems}
        />
      ) : (
        <List>{menuItems.map(renderMenuItem)}</List>
      )}
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidthCurrent },
        flexShrink: { sm: 0 },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: drawerTransitionDuration.enter,
        }),
      }}
      aria-label="mailbox folders"
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            // backgroundColor: theme.palette.background.darker,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: expandedDrawerWidth,
              backgroundColor: theme.palette.background.darker,
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthCurrent,
              backgroundColor: theme.palette.background.darker,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: drawerTransitionDuration.enter,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
}

export default Sidebar;
